import { BottomSheetBackdrop, useBottomSheet } from '@gorhom/bottom-sheet';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { Platform, Pressable, StyleSheet, type ViewProps } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedReaction,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

/**
 * Platform-specific bottom sheet backdrop used by `ContextSheet`.
 *
 * **Android (Expo SDK 55+):** `@gorhom/bottom-sheet`'s default backdrop closes the sheet with
 * react-native-gesture-handler's `Gesture.Tap()`. After the SDK/RNGH upgrade, that tap gesture
 * sometimes fired together with presses on sheet content (buttons, list rows, etc.), so the
 * control's `onPress` ran and the sheet dismissed in the same interaction. Workarounds like
 * `pressBehavior="none"` removed tap-outside-to-close entirely.
 *
 * This module keeps the same fade and `pressBehavior` semantics as Gorhom's backdrop, but the
 * dismiss target is a Reanimated-wrapped React Native `Pressable`, which participates in the
 * normal touch responder chain. Only taps on the dimmed area behind the sheet receive `onPress`,
 * so tap-outside-to-close works without conflicting with interactions on the sheet body.
 *
 * **iOS:** Delegates to Gorhom's `BottomSheetBackdrop` unchanged.
 */

type IBottomSheetBackdropProps = ComponentProps<typeof BottomSheetBackdrop>;

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const styles = StyleSheet.create({
  backdrop: {
    backgroundColor: 'black',
  },
});

const GorhomBackdropDefaults = {
  opacity: 0.5,
  appearsOnIndex: 1,
  disappearsOnIndex: 0,
  enableTouchThrough: false,
} as const;

const DefaultAccessibilityLabel = 'Bottom sheet backdrop';

/** Android-only: mirrors Gorhom backdrop behavior with RN `Pressable` instead of RNGH `Gesture.Tap()`. */
const BottomSheetAndroidPressableBackdropComponent = ({
  animatedIndex,
  opacity: providedOpacity,
  appearsOnIndex: providedAppearsOnIndex,
  disappearsOnIndex: providedDisappearsOnIndex,
  enableTouchThrough: providedEnableTouchThrough,
  pressBehavior = 'close',
  onPress,
  style,
  children,
  accessible = true,
  accessibilityRole = 'button',
  accessibilityLabel = DefaultAccessibilityLabel,
  accessibilityHint,
}: IBottomSheetBackdropProps) => {
  const { snapToIndex, close } = useBottomSheet();
  const isMounted = useRef(false);

  const opacity = providedOpacity ?? GorhomBackdropDefaults.opacity;
  const appearsOnIndex = providedAppearsOnIndex ?? GorhomBackdropDefaults.appearsOnIndex;
  const disappearsOnIndex = providedDisappearsOnIndex ?? GorhomBackdropDefaults.disappearsOnIndex;
  const enableTouchThrough =
    providedEnableTouchThrough ?? GorhomBackdropDefaults.enableTouchThrough;

  const [pointerEvents, setPointerEvents] = useState<ViewProps['pointerEvents']>(
    enableTouchThrough ? 'none' : 'auto'
  );

  const handleBackdropPress = useCallback(() => {
    onPress?.();
    if (pressBehavior === 'close') {
      close();
    } else if (pressBehavior === 'collapse') {
      snapToIndex(disappearsOnIndex as number);
    } else if (typeof pressBehavior === 'number') {
      snapToIndex(pressBehavior);
    }
  }, [snapToIndex, close, disappearsOnIndex, pressBehavior, onPress]);

  const handleContainerTouchability = useCallback((shouldDisableTouchability: boolean) => {
    if (isMounted.current) {
      setPointerEvents(shouldDisableTouchability ? 'none' : 'auto');
    }
  }, []);

  const resolvedAccessible = accessible ?? true;
  const resolvedRole = accessibilityRole ?? 'button';
  const resolvedLabel = accessibilityLabel ?? DefaultAccessibilityLabel;

  const containerAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: interpolate(
        animatedIndex.value,
        [-1, disappearsOnIndex, appearsOnIndex],
        [0, 0, opacity],
        Extrapolation.CLAMP
      ),
    }),
    [animatedIndex, appearsOnIndex, disappearsOnIndex, opacity]
  );

  const pressableStyle = useMemo(
    () => [StyleSheet.absoluteFill, styles.backdrop, style, containerAnimatedStyle],
    [style, containerAnimatedStyle]
  );

  // Sync pointerEvents with Gorhom: disable touches when sheet is fully closed (animatedIndex <= disappearsOnIndex).
  useAnimatedReaction(
    () => animatedIndex.value <= disappearsOnIndex,
    (shouldDisableTouchability, previous) => {
      if (shouldDisableTouchability === previous) {
        return;
      }
      scheduleOnRN(handleContainerTouchability, shouldDisableTouchability);
    },
    [disappearsOnIndex, handleContainerTouchability]
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (pressBehavior === 'none') {
    return (
      <Animated.View
        style={pressableStyle}
        pointerEvents={pointerEvents}
        accessible={resolvedAccessible}
        accessibilityRole={resolvedRole}
        accessibilityLabel={resolvedLabel}
        accessibilityHint={accessibilityHint ?? undefined}>
        {children}
      </Animated.View>
    );
  }

  return (
    <AnimatedPressable
      style={pressableStyle}
      pointerEvents={pointerEvents}
      onPress={handleBackdropPress}
      android_ripple={null}
      accessible={resolvedAccessible}
      accessibilityRole={resolvedRole}
      accessibilityLabel={resolvedLabel}
      accessibilityHint={
        accessibilityHint ??
        `Tap to ${typeof pressBehavior === 'string' ? pressBehavior : 'move'} the Bottom Sheet`
      }>
      {children}
    </AnimatedPressable>
  );
};

const BottomSheetAndroidPressableBackdrop = memo(BottomSheetAndroidPressableBackdropComponent);

export function BottomSheetPlatformBackdrop(props: IBottomSheetBackdropProps) {
  if (Platform.OS === 'android') {
    return <BottomSheetAndroidPressableBackdrop {...props} />;
  }
  return <BottomSheetBackdrop {...props} />;
}
